import { useState, useEffect, useCallback } from "react";
import {
  Loader2, LayoutDashboard, Settings2, Globe2, ShieldCheck, AlertTriangle, CheckCircle2,
  ArrowRight, History, ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "./ui/accordion";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { CommercialPlans } from "./CommercialPlans";
import { CommercialRegions } from "./CommercialRegions";
import { PLAN_KEYS, PLAN_LABELS, fmtDate } from "../lib/commercial";

const Pilotage = ({ goTo }) => {
  const [readiness, setReadiness] = useState(null);
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rd, pubs] = await Promise.all([
        api.get("/admin/commercial/readiness"),
        api.get("/admin/commercial/publications"),
      ]);
      setReadiness(rd.data);
      setPublications(pubs.data.publications || []);
    } catch (e) { toast.error("Impossible de charger l'état du catalogue."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const publish = async () => {
    setPublishing(true);
    try {
      await api.post("/admin/commercial/publications", { plan_versions: readiness.suggested_plan_versions });
      toast.success("Catalogue publié : offres publiques mises à jour (droits des abonnements inchangés).");
      setConfirm(false);
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && Array.isArray(d.errors)) {
        setConfirm(false);
        toast.error(apiError(d));
        await load();
      } else {
        toast.error(apiError(d) || "Échec de la publication.");
      }
    } finally { setPublishing(false); }
  };

  if (loading || !readiness) {
    return <div className="flex items-center justify-center py-16 text-slate-500"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  const pub = readiness.active_publication;
  const anomalies = readiness.anomalies || [];

  return (
    <div className="space-y-6" data-testid="commercial-pilotage">
      {/* Catalogue actif */}
      <div className="glass rounded-2xl p-5 border border-slate-200" data-testid="active-publication">
        <div className="flex items-center gap-2 mb-2"><ShieldCheck className="w-4 h-4 text-emerald-600" /><h3 className="font-heading text-lg font-semibold">Catalogue actif</h3></div>
        {pub ? (
          <div className="text-sm text-slate-600 space-y-1">
            <div>Publié le <span className="font-medium text-slate-800">{fmtDate(pub.created_at)}</span></div>
            <div className="text-xs text-slate-400">Réf. {pub.id}</div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {PLAN_KEYS.map((k) => {
                const ps = pub.snapshot?.plans?.[k];
                return <Badge key={k} variant="outline" className="bg-slate-100 text-slate-600 border-slate-300">{PLAN_LABELS[k]} v{ps?.version ?? "—"}</Badge>;
              })}
            </div>
            <div className="text-xs text-slate-400 mt-1">Régions : {(pub.snapshot?.regions || []).filter((r) => r.active).map((r) => r.id).join(", ") || "—"}</div>
          </div>
        ) : (
          <p className="text-sm text-amber-600" data-testid="no-active-publication">Aucune publication active — le catalogue public utilise le système legacy (fallback).</p>
        )}
      </div>

      {/* Préparation / publication */}
      <div className="glass rounded-2xl p-5 border border-slate-200" data-testid="publication-readiness">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h3 className="font-heading text-lg font-semibold">Préparer la prochaine publication</h3>
          {readiness.publishable ? (
            <Badge data-testid="readiness-ok" variant="outline" className="bg-emerald-500/10 text-emerald-700 border-emerald-400/40 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Prêt à publier</Badge>
          ) : (
            <Badge data-testid="readiness-blocked" variant="outline" className="bg-amber-500/10 text-amber-700 border-amber-400/40 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Publication bloquée</Badge>
          )}
        </div>

        {anomalies.length > 0 ? (
          <div className="space-y-2 mb-4" data-testid="anomalies-list">
            <p className="text-sm text-slate-600">Corrigez les points suivants avant de publier :</p>
            {anomalies.map((a, i) => (
              <div key={i} data-testid={`anomaly-${a.type}-${i}`} className="flex items-start justify-between gap-3 rounded-lg border border-amber-300/60 bg-amber-50 px-3 py-2.5">
                <div className="flex items-start gap-2 min-w-0">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                  <span className="text-sm text-amber-900">{a.message}</span>
                </div>
                <Button data-testid={`anomaly-goto-${i}`} size="sm" variant="outline" className="border-amber-300 text-amber-800 shrink-0"
                  onClick={() => goTo(a.target)}>
                  {a.target === "plans" ? "Plans commerciaux" : "Tarifs & régions"} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-emerald-700 mb-4 inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Tous les prérequis sont remplis : {readiness.active_region_count} région(s) active(s), chaque plan dispose d'une version préparée.</p>
        )}

        <Button data-testid="publish-catalog-btn" disabled={!readiness.publishable} onClick={() => setConfirm(true)}
          className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold disabled:opacity-50">
          <ShieldCheck className="w-4 h-4 mr-2" /> Préparer et publier le catalogue
        </Button>

        {/* Historique des publications — repliable */}
        <Accordion type="single" collapsible className="mt-5">
          <AccordionItem value="history" data-testid="publications-history" className="border border-slate-200 rounded-xl px-4">
            <AccordionTrigger className="text-sm font-semibold">
              <span className="flex items-center gap-2"><History className="w-4 h-4 text-emerald-600" /> Historique des publications</span>
            </AccordionTrigger>
            <AccordionContent>
              {publications.length === 0 ? (
                <p className="text-sm text-slate-400 py-2">Aucune publication pour le moment.</p>
              ) : (
                <div className="space-y-2 pb-2">
                  {publications.map((p) => (
                    <div key={p.id} data-testid={`publication-row-${p.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 border border-slate-200 px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={p.status === "active" ? "bg-emerald-500/10 text-emerald-700 border-emerald-400/40" : "bg-slate-500/10 text-slate-500 border-slate-400/30"}>
                          {p.status === "active" ? "Active" : "Remplacée"}
                        </Badge>
                        <span className="text-xs text-slate-500">{fmtDate(p.created_at)}</span>
                        <span className="text-[11px] text-slate-400">· {(p.snapshot?.regions || []).map((r) => r.id).join(", ")}</span>
                      </div>
                      <span className="text-[11px] text-slate-400">{p.id}</span>
                    </div>
                  ))}
                </div>
              )}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      {/* Accès aux sous-pages */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <button data-testid="goto-plans" onClick={() => goTo("plans")} className="glass rounded-2xl p-5 border border-slate-200 text-left hover:border-emerald-400 transition-colors">
          <div className="flex items-center gap-2 mb-1"><Settings2 className="w-4 h-4 text-emerald-600" /><span className="font-semibold">Plans commerciaux</span></div>
          <p className="text-sm text-slate-500">Définir ce que contient chaque plan (volumes, capacités, inclusions, versions).</p>
        </button>
        <button data-testid="goto-regions" onClick={() => goTo("regions")} className="glass rounded-2xl p-5 border border-slate-200 text-left hover:border-emerald-400 transition-colors">
          <div className="flex items-center gap-2 mb-1"><Globe2 className="w-4 h-4 text-emerald-600" /><span className="font-semibold">Tarifs &amp; régions</span></div>
          <p className="text-sm text-slate-500">Gérer les marchés, devises et tarifs par région (prix, utilisateurs, sur devis).</p>
        </button>
      </div>

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent data-testid="confirm-publish-dialog">
          <DialogHeader>
            <DialogTitle>Publier le catalogue</DialogTitle>
            <DialogDescription>Cette publication modifiera les offres et tarifs visibles publiquement. Elle ne modifie pas les droits des abonnements existants.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="border-slate-200" onClick={() => setConfirm(false)}>Annuler</Button>
            <Button data-testid="confirm-publish-ok" onClick={publish} disabled={publishing} className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold">
              {publishing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />} Publier maintenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SUBS = [
  { key: "home", label: "Configuration", icon: LayoutDashboard },
  { key: "plans", label: "Plans commerciaux", icon: Settings2 },
  { key: "regions", label: "Tarifs & régions", icon: Globe2 },
];

export const CommercialCatalog = () => {
  const [sub, setSub] = useState("home");
  return (
    <div data-testid="commercial-catalog" className="space-y-5">
      <div className="flex flex-wrap gap-1.5 p-1 rounded-xl bg-slate-100 border border-slate-200 w-fit" data-testid="commercial-subnav">
        {SUBS.map((s) => (
          <button key={s.key} data-testid={`subnav-${s.key}`} onClick={() => setSub(s.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${sub === s.key ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
            <s.icon className="w-4 h-4" /> {s.label}
          </button>
        ))}
      </div>
      {sub === "home" && <Pilotage goTo={setSub} />}
      {sub === "plans" && <CommercialPlans />}
      {sub === "regions" && <CommercialRegions />}
    </div>
  );
};
